import React, { useRef, useState, useEffect, useCallback } from "react"
import { Box } from "./Box"

export interface HorizontalScrollShadowProps {
	children: React.ReactNode

	shadowWidth?: number

	shadowColor?: string

	shadowOpacity?: number

	showLeftShadow?: boolean

	showRightShadow?: boolean

	threshold?: number

	className?: string
}

/**
 * HorizontalScrollShadow
 *
 * A container that adds gradient shadows on the left/right edges when content
 * is scrollable horizontally. Shadows appear/disappear dynamically based on
 * scroll position, providing visual feedback that more content is available.
 *
 */
export function HorizontalScrollShadow({
	children,
	shadowWidth = 35,
	shadowColor = "0, 0, 0",
	shadowOpacity = 0.18,
	showLeftShadow: controlledLeftShadow,
	showRightShadow: controlledRightShadow,
	threshold = 5,
	className,
}: HorizontalScrollShadowProps) {
	const scrollRef = useRef<HTMLDivElement>(null)
	const [shadows, setShadows] = useState({ left: false, right: false })

	// Update shadow visibility based on scroll position
	const updateShadows = useCallback(() => {
		const element = scrollRef.current
		if (!element) return

		const { scrollLeft, scrollWidth, clientWidth } = element

		// Calculate both shadow states in one pass
		const left = scrollLeft > threshold
		const right = scrollLeft < scrollWidth - clientWidth - threshold

		setShadows((prev) => {
			if (prev.left !== left || prev.right !== right) {
				return { left, right }
			}
			return prev
		})
	}, [threshold])

	useEffect(() => {
		const element = scrollRef.current
		if (!element) return

		// Initial check
		updateShadows()

		// Throttle scroll events using requestAnimationFrame
		let rafId: number | null = null
		const handleScroll = () => {
			if (rafId === null) {
				rafId = requestAnimationFrame(() => {
					updateShadows()
					rafId = null
				})
			}
		}

		const resizeObserver = new ResizeObserver(updateShadows)

		element.addEventListener("scroll", handleScroll, { passive: true })
		resizeObserver.observe(element)

		return () => {
			if (rafId !== null) {
				cancelAnimationFrame(rafId)
			}
			element.removeEventListener("scroll", handleScroll)
			resizeObserver.disconnect()
		}
	}, [updateShadows])

	const showLeft =
		controlledLeftShadow !== undefined ? controlledLeftShadow : shadows.left
	const showRight =
		controlledRightShadow !== undefined ? controlledRightShadow : shadows.right

	const shadowStyle = (direction: "left" | "right") => ({
		zIndex: 1,
		height: "100%",
		width: `${shadowWidth}px`,
		background: `linear-gradient(to ${direction}, transparent, rgba(${shadowColor}, ${shadowOpacity}))`,
	})

	return (
		<Box position="relative" className={className}>
			{/* Left Shadow - indicates you can scroll left */}
			{showLeft && (
				<Box
					position="absolute"
					left="0"
					top="0"
					roundedRight="md"
					pointerEvents="none"
					aria-hidden="true"
					style={shadowStyle("left")}
				/>
			)}

			{/* Scrollable Content */}
			<Box
				ref={scrollRef}
				overflowX="auto"
				role="region"
				aria-label="Horizontally scrollable content"
			>
				{children}
			</Box>

			{/* Right Shadow - indicates you can scroll right */}
			{showRight && (
				<Box
					position="absolute"
					right="0"
					top="0"
					pointerEvents="none"
					roundedLeft="md"
					aria-hidden="true"
					style={shadowStyle("right")}
				/>
			)}
		</Box>
	)
}
